import { supabase } from '../lib/supabase';

/**
 * Maps a raw CSV/Excel row to the work_orders table schema.
 */
function mapRowToWorkOrder(row) {
  const pick = (keys) => {
    for (const key of keys) {
      const found = Object.keys(row)?.find(k => k?.trim()?.toLowerCase() === key?.toLowerCase());
      if (found !== undefined && row?.[found] !== undefined && row?.[found] !== '') {
        return row?.[found];
      }
    }
    return null;
  };

  const rawDate = pick(['Date', 'date', 'DATE']);
  let parsedDate = null;
  if (rawDate) {
    if (rawDate instanceof Date) {
      // Handle JS Date objects returned by XLSX when cellDates: true
      parsedDate = isNaN(rawDate?.getTime()) ? null : rawDate?.toISOString()?.split('T')?.[0];
    } else if (typeof rawDate === 'number') {
      const excelEpoch = new Date(1899, 11, 30);
      const d = new Date(excelEpoch.getTime() + rawDate * 86400000);
      parsedDate = d?.toISOString()?.split('T')?.[0];
    } else {
      const d = new Date(rawDate);
      parsedDate = isNaN(d?.getTime()) ? null : d?.toISOString()?.split('T')?.[0];
    }
  }

  const rawBreakdown = pick(['BREAKDOWN TIME (MINS)', 'Breakdown Time (mins)', 'Breakdown Time', 'breakdown_time_mins', 'breakdown time (mins)', 'breakdown time']);
  const breakdownMins = rawBreakdown !== null ? parseFloat(rawBreakdown) : null;

  return {
    date: parsedDate,
    shift: pick(['Shift', 'shift', 'SHIFT']) || null,
    shift_incharge: pick(['Shift Incharge', 'Shift Incharge', 'Shift In-charge', 'shift_incharge', 'shift incharge', 'SHIFT INCHARGE']) || null,
    equipment: pick(['Equipment', 'EQUIPMENT', 'Equipment ID', 'Equipment Id', 'equipment_id', 'EquipmentID', 'equipment id']) || null,
    occurrence: pick(['OCCURRENCE', 'Occurrence', 'occurrence', 'Occupance', 'occupance', 'Occupancy', 'occupancy']) || null,
    breakdown_time_mins: isNaN(breakdownMins) ? null : breakdownMins,
    phenomenon: pick(['PHENOMENON', 'Phenomenon', 'phenomenon', 'Description', 'description', 'DESCRIPTION']) || null,
    counter_measure: pick(['COUNTER MEASURE', 'Counter Measure', 'counter measure', 'COUNTERMEASURE', 'Countermeasure', 'countermeasure', 'Solution', 'solution', 'SOLUTION']) || null,
    spare_consumed: pick(['SPARE CONSUMED', 'Spare Consumed', 'spare_consumed', 'Spare', 'spare consumed']) || null,
    status: pick(['STATUS', 'Status', 'status']) || null,
    why_why_analysis: pick(['WHY-WHY ANALYSIS', 'Why-Why Analysis', 'Why Why Analysis', 'why_why_analysis', 'WhyWhy', 'why-why analysis']) || null,
    root_cause: pick(['ROOT CAUSE', 'Root Cause', 'root_cause', 'RootCause', 'root cause']) || null,
  };
}

/**
 * Checks if a value is NULL/empty/invalid (Nil, NIL, NULL, "", " ", "-", etc.)
 */
export function isNullValue(val) {
  if (val === null || val === undefined) return true;
  const s = String(val)?.trim()?.toLowerCase();
  return s === '' || s === 'nil' || s === 'null' || s === '-' || s === 'n/a' || s === 'na' || s === 'nill';
}

/**
 * Normalizes text: trim + uppercase for consistent grouping
 */
export function normalizeText(val) {
  if (isNullValue(val)) return null;
  return String(val)?.trim()?.toUpperCase();
}

/**
 * Fuzzy-groups similar root causes / descriptions using keyword matching.
 * Returns a canonical category name.
 */
export function canonicalizeRootCause(raw) {
  if (!raw) return null;
  const s = raw?.trim()?.toUpperCase();

  // Pressure switch variants
  if (/PRESSURE\s*SW(ITCH)?/?.test(s) || /PRESS\s*SW/?.test(s)) return 'PRESSURE SWITCH FAILURE';
  // UPS variants
  if (/\bUPS\b/?.test(s)) return 'UPS FAULT';
  // Thread / tightening
  if (/THREAD|TORQUE|TIGHTEN|FITTING/?.test(s)) return 'IMPROPER TIGHTENING / THREAD DAMAGE';
  // Leakage
  if (/LEAK(AGE)?|LEAKING/?.test(s)) return 'LEAKAGE';
  // Sensor
  if (/SENSOR|PROXIMITY|PROX/?.test(s)) return 'SENSOR FAILURE';
  // Motor
  if (/MOTOR|WINDING/?.test(s)) return 'MOTOR FAILURE';
  // Belt / chain
  if (/BELT|CHAIN|DRIVE/?.test(s)) return 'BELT / CHAIN FAILURE';
  // Electrical
  if (/ELECTRICAL|WIRING|FUSE|RELAY|CONTACTOR|CIRCUIT/?.test(s)) return 'ELECTRICAL FAULT';
  // Pneumatic
  if (/PNEUMATIC|AIR|CYLINDER|VALVE/?.test(s)) return 'PNEUMATIC FAILURE';
  // Hydraulic
  if (/HYDRAULIC|OIL|PUMP/?.test(s)) return 'HYDRAULIC FAILURE';
  // Bearing
  if (/BEARING|SHAFT/?.test(s)) return 'BEARING / SHAFT FAILURE';
  // Wear
  if (/WEAR|WORN|ABRASION/?.test(s)) return 'WEAR & TEAR';
  // Maintenance
  if (/MAINTENANCE|PREVENTIVE|PM|LUBRICATION|LUBE/?.test(s)) return 'MAINTENANCE RELATED';
  // Human error
  if (/HUMAN|OPERATOR|SKILL|TRAINING|IMPROPER|WRONG/?.test(s)) return 'HUMAN ERROR / SKILL GAP';
  // Software / PLC
  if (/SOFTWARE|PLC|PROGRAM|CONTROL/?.test(s)) return 'SOFTWARE / PLC ISSUE';

  // Return cleaned version if no match
  return s?.length > 60 ? s?.substring(0, 60) + '...' : s;
}

/**
 * Imports an array of raw rows into the work_orders table.
 */
export async function importWorkOrdersToSupabase(rows, onProgress = null) {
  if (!rows || rows?.length === 0) {
    return { inserted: 0, errors: ['No rows to import'] };
  }

  // ── DEBUG: Log actual column names from first row ──
  if (rows?.length > 0) {
    const sampleKeys = Object.keys(rows?.[0]);
    console.log('🔍 [WorkOrderImport] Raw column names from Excel (first row keys):', sampleKeys);
    console.log('🔍 [WorkOrderImport] First raw row sample:', rows?.[0]);
  }

  const mapped = rows?.map(mapRowToWorkOrder)?.filter(r => r?.equipment || r?.phenomenon || r?.date);

  // ── DEBUG: Log mapping results ──
  console.log(`🔍 [WorkOrderImport] Total raw rows: ${rows?.length}`);
  console.log(`🔍 [WorkOrderImport] Rows passing filter (equipment OR phenomenon OR date): ${mapped?.length}`);
  if (mapped?.length > 0) {
    console.log('🔍 [WorkOrderImport] First mapped row:', mapped?.[0]);
    // Count how many have each key populated
    const fieldStats = {};
    const fields = ['date', 'shift', 'shift_incharge', 'equipment', 'occurrence', 'breakdown_time_mins', 'phenomenon', 'counter_measure', 'spare_consumed', 'status', 'why_why_analysis', 'root_cause'];
    fields?.forEach(f => {
      fieldStats[f] = mapped?.filter(r => r?.[f] !== null && r?.[f] !== undefined)?.length;
    });
    console.log('🔍 [WorkOrderImport] Field population stats (non-null count out of', mapped?.length, '):', fieldStats);
  } else {
    console.warn('⚠️ [WorkOrderImport] ALL rows were filtered out! Check column name matching.');
    // Show what the mapped rows look like before filtering
    const allMapped = rows?.slice(0, 3)?.map(mapRowToWorkOrder);
    console.log('⚠️ [WorkOrderImport] Sample mapped rows (before filter):', allMapped);
  }

  const BATCH_SIZE = 500;
  let inserted = 0;
  const errors = [];

  for (let i = 0; i < mapped?.length; i += BATCH_SIZE) {
    const batch = mapped?.slice(i, i + BATCH_SIZE);
    try {
      const { data, error } = await supabase?.from('work_orders')?.insert(batch)?.select('id');
      if (error) {
        console.error(`❌ [WorkOrderImport] Batch ${Math.floor(i / BATCH_SIZE) + 1} insert error:`, error);
        errors?.push(`Batch ${Math.floor(i / BATCH_SIZE) + 1}: ${error?.message}`);
      } else {
        inserted += batch?.length;
        console.log(`✅ [WorkOrderImport] Batch ${Math.floor(i / BATCH_SIZE) + 1} inserted ${batch?.length} rows. Total so far: ${inserted}`);
      }
    } catch (err) {
      console.error(`❌ [WorkOrderImport] Batch ${Math.floor(i / BATCH_SIZE) + 1} exception:`, err);
      errors?.push(`Batch ${Math.floor(i / BATCH_SIZE) + 1}: ${err?.message}`);
    }
    if (onProgress) onProgress(Math.min(inserted, mapped?.length), mapped?.length);
  }

  console.log(`🏁 [WorkOrderImport] Import complete. Inserted: ${inserted}, Errors: ${errors?.length}`);
  return { inserted, errors };
}

/**
 * Fetches ALL work orders from Supabase, bypassing the 1000-row default limit
 * by paginating through all records in batches of 1000.
 * @returns {Promise<Array>}
 */
export async function fetchAllWorkOrders() {
  const PAGE_SIZE = 1000;
  let allRecords = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      ?.from('work_orders')
      ?.select('*')
      ?.order('date', { ascending: false })
      ?.range(from, from + PAGE_SIZE - 1);

    if (error) {
      console.error('Error fetching work orders page:', error);
      break;
    }

    if (!data || data?.length === 0) {
      hasMore = false;
    } else {
      allRecords = allRecords?.concat(data);
      if (data?.length < PAGE_SIZE) {
        hasMore = false;
      } else {
        from += PAGE_SIZE;
      }
    }
  }

  return allRecords;
}

/**
 * Returns the count of records in the work_orders table.
 */
export async function getWorkOrderCount() {
  const { count, error } = await supabase?.from('work_orders')?.select('*', { count: 'exact', head: true });
  if (error) return 0;
  return count || 0;
}

/**
 * Deletes ALL records from the work_orders table.
 */
export async function clearWorkOrdersTable() {
  const { error } = await supabase?.from('work_orders')?.delete()?.not('id', 'is', null);
  if (error) {
    return { success: false, error: error?.message };
  }
  return { success: true, error: null };
}

/**
 * Computes full aggregations from the work_orders table for Analytics Dashboard.
 */
export async function fetchWorkOrderAnalytics() {
  const workOrders = await fetchAllWorkOrders();
  if (!workOrders || workOrders?.length === 0) {
    return { workOrders: [], assets: [] };
  }
  return { workOrders, assets: [] };
}

/**
 * Computes a rich, cleaned, normalized aggregated summary for the AI-powered analytics dashboard.
 * - Fixes 1000-row limit via pagination
 * - Cleans NULL/Nil/empty values
 * - Normalizes and fuzzy-groups root causes
 * - Computes all KPIs, charts, and AI-ready data
 */
export async function fetchWorkOrderSummaryForAI() {
  const workOrders = await fetchAllWorkOrders();

  if (!workOrders || workOrders?.length === 0) {
    return null;
  }

  const total = workOrders?.length;

  // ── Breakdown time: only valid numeric values ──
  const withBreakdown = workOrders?.filter(wo => {
    const v = wo?.breakdown_time_mins;
    return v !== null && v !== undefined && !isNaN(parseFloat(v)) && parseFloat(v) > 0;
  });

  const totalBreakdownTime = withBreakdown?.reduce((sum, wo) => sum + parseFloat(wo?.breakdown_time_mins), 0);
  const avgBreakdownTime = withBreakdown?.length > 0
    ? (totalBreakdownTime / withBreakdown?.length)?.toFixed(1)
    : 0;

  const mttr = avgBreakdownTime;

  // Breakdown % — use occurrence field if available, else count all records
  const breakdownPct = '100';

  // ── Status breakdown (cleaned) ──
  const byStatus = {};
  workOrders?.forEach(wo => {
    const s = normalizeText(wo?.status);
    if (!s) return;
    byStatus[s] = (byStatus?.[s] || 0) + 1;
  });

  // ── Equipment frequency (cleaned) ──
  const byEquipment = {};
  const equipmentDowntime = {};
  workOrders?.forEach(wo => {
    const eq = normalizeText(wo?.equipment);
    if (!eq) return;
    byEquipment[eq] = (byEquipment?.[eq] || 0) + 1;
    const bt = parseFloat(wo?.breakdown_time_mins);
    if (!isNaN(bt) && bt > 0) {
      equipmentDowntime[eq] = (equipmentDowntime?.[eq] || 0) + bt;
    }
  });
  const topEquipment = Object.entries(byEquipment)
    ?.sort((a, b) => b?.[1] - a?.[1])
    ?.slice(0, 15)
    ?.map(([id, count]) => ({
      equipment_id: id,
      count,
      total_downtime: Math.round(equipmentDowntime?.[id] || 0),
    }));

  // Total unique equipment count (before slicing to top 15)
  const totalUniqueEquipment = Object.keys(byEquipment)?.length;

  // Worst equipment = highest downtime
  const worstEquipment = Object.entries(equipmentDowntime)
    ?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] || topEquipment?.[0]?.equipment_id || 'N/A';

  // ── Root cause: cleaned + canonicalized ──
  const rawRootCauseMap = {};
  const rootCauseDowntime = {};
  workOrders?.forEach(wo => {
    const rc = wo?.root_cause;
    if (isNullValue(rc)) return;
    const canonical = canonicalizeRootCause(rc);
    if (!canonical) return;
    rawRootCauseMap[canonical] = (rawRootCauseMap?.[canonical] || 0) + 1;
    const bt = parseFloat(wo?.breakdown_time_mins);
    if (!isNaN(bt) && bt > 0) {
      rootCauseDowntime[canonical] = (rootCauseDowntime?.[canonical] || 0) + bt;
    }
  });
  const topRootCauses = Object.entries(rawRootCauseMap)
    ?.sort((a, b) => b?.[1] - a?.[1])
    ?.slice(0, 15)
    ?.map(([cause, count]) => ({
      root_cause: cause,
      count,
      total_downtime: Math.round(rootCauseDowntime?.[cause] || 0),
      pct: total > 0 ? ((count / total) * 100)?.toFixed(1) : '0',
    }));

  const topRootCause = topRootCauses?.[0]?.root_cause || 'N/A';

  // ── Pareto: root causes sorted by downtime ──
  const paretoRootCauses = [...topRootCauses]
    ?.sort((a, b) => b?.total_downtime - a?.total_downtime)
    ?.slice(0, 10);

  // ── Root cause vs Equipment matrix ──
  const rcEquipmentMatrix = {};
  workOrders?.forEach(wo => {
    const rc = wo?.root_cause;
    const eq = normalizeText(wo?.equipment);
    if (isNullValue(rc) || !eq) return;
    const canonical = canonicalizeRootCause(rc);
    if (!canonical) return;
    if (!rcEquipmentMatrix?.[canonical]) rcEquipmentMatrix[canonical] = {};
    rcEquipmentMatrix[canonical][eq] = (rcEquipmentMatrix?.[canonical]?.[eq] || 0) + 1;
  });

  // ── Shift breakdown (cleaned) ──
  const byShift = {};
  const shiftDowntime = {};
  const shiftMTTR = {};
  workOrders?.forEach(wo => {
    const sh = normalizeText(wo?.shift);
    if (!sh) return;
    byShift[sh] = (byShift?.[sh] || 0) + 1;
    const bt = parseFloat(wo?.breakdown_time_mins);
    if (!isNaN(bt) && bt > 0) {
      shiftDowntime[sh] = (shiftDowntime?.[sh] || 0) + bt;
    }
  });
  Object.keys(byShift)?.forEach(sh => {
    shiftMTTR[sh] = byShift?.[sh] > 0
      ? ((shiftDowntime?.[sh] || 0) / byShift?.[sh])?.toFixed(1)
      : '0';
  });

  // ── Shift incharge MTTR ──
  const inchargeMap = {};
  const inchargeDowntime = {};
  workOrders?.forEach(wo => {
    const ic = normalizeText(wo?.shift_incharge);
    if (!ic) return;
    inchargeMap[ic] = (inchargeMap?.[ic] || 0) + 1;
    const bt = parseFloat(wo?.breakdown_time_mins);
    if (!isNaN(bt) && bt > 0) {
      inchargeDowntime[ic] = (inchargeDowntime?.[ic] || 0) + bt;
    }
  });
  const inchargeMTTR = Object.keys(inchargeMap)?.map(ic => ({
    name: ic,
    count: inchargeMap?.[ic],
    total_downtime: Math.round(inchargeDowntime?.[ic] || 0),
    mttr: inchargeMap?.[ic] > 0
      ? ((inchargeDowntime?.[ic] || 0) / inchargeMap?.[ic])?.toFixed(1)
      : '0',
  }))?.sort((a, b) => parseFloat(b?.mttr) - parseFloat(a?.mttr));

  // ── Spare consumed (cleaned, only valid entries) ──
  const spareMap = {};
  workOrders?.forEach(wo => {
    const sp = normalizeText(wo?.spare_consumed);
    if (!sp) return;
    spareMap[sp] = (spareMap?.[sp] || 0) + 1;
  });

  // ── Monthly trend ──
  const byMonth = {};
  const monthlyDowntime = {};
  workOrders?.forEach(wo => {
    if (wo?.date) {
      const d = new Date(wo.date);
      if (!isNaN(d?.getTime())) {
        const key = `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}`;
        byMonth[key] = (byMonth?.[key] || 0) + 1;
        const bt = parseFloat(wo?.breakdown_time_mins);
        if (!isNaN(bt) && bt > 0) {
          monthlyDowntime[key] = (monthlyDowntime?.[key] || 0) + bt;
        }
      }
    }
  });

  // ── Outlier detection: breakdown times > 2 std deviations ──
  const btValues = withBreakdown?.map(wo => parseFloat(wo?.breakdown_time_mins));
  const btMean = btValues?.length > 0 ? btValues?.reduce((a, b) => a + b, 0) / btValues?.length : 0;
  const btStd = btValues?.length > 1
    ? Math.sqrt(btValues?.reduce((sum, v) => sum + Math.pow(v - btMean, 2), 0) / btValues?.length)
    : 0;
  const outlierThreshold = btMean + 2 * btStd;
  const outliers = withBreakdown
    ?.filter(wo => parseFloat(wo?.breakdown_time_mins) > outlierThreshold)
    ?.map(wo => ({
      equipment_id: normalizeText(wo?.equipment) || 'UNKNOWN',
      breakdown_time_mins: parseFloat(wo?.breakdown_time_mins),
      date: wo?.date,
      root_cause: canonicalizeRootCause(wo?.root_cause) || 'UNKNOWN',
    }))
    ?.sort((a, b) => b?.breakdown_time_mins - a?.breakdown_time_mins)
    ?.slice(0, 10);

  // ── Equipment risk scoring ──
  const equipmentRisk = topEquipment?.slice(0, 10)?.map(eq => {
    const downtimeScore = Math.min((eq?.total_downtime / (totalBreakdownTime || 1)) * 100, 100);
    const freqScore = Math.min((eq?.count / total) * 100, 100);
    const riskScore = (downtimeScore * 0.6 + freqScore * 0.4);
    return {
      equipment_id: eq?.equipment_id,
      count: eq?.count,
      total_downtime: eq?.total_downtime,
      risk_score: riskScore?.toFixed(1),
      risk_level: riskScore > 40 ? 'HIGH' : riskScore > 20 ? 'MEDIUM' : 'LOW',
    };
  });

  return {
    total,
    breakdownCount: total,
    breakdownPct,
    totalBreakdownTime: Math.round(totalBreakdownTime),
    avgBreakdownTime,
    mttr,
    worstEquipment,
    topRootCause,
    byStatus,
    byType: {},
    topEquipment,
    totalUniqueEquipment,
    worstEquipmentList: topEquipment?.slice(0, 5),
    topRootCauses,
    paretoRootCauses,
    rcEquipmentMatrix,
    byShift,
    shiftDowntime,
    shiftMTTR,
    inchargeMTTR: inchargeMTTR?.slice(0, 10),
    spareMap,
    byMonth,
    monthlyDowntime,
    outliers,
    equipmentRisk,
    sampleRecords: workOrders?.slice(0, 20)?.map(wo => ({
      date: wo?.date,
      equipment_id: normalizeText(wo?.equipment),
      breakdown_time_mins: wo?.breakdown_time_mins,
      status: normalizeText(wo?.status),
      root_cause: canonicalizeRootCause(wo?.root_cause),
      shift: normalizeText(wo?.shift),
      shift_incharge: normalizeText(wo?.shift_incharge),
      phenomenon: normalizeText(wo?.phenomenon),
      counter_measure: normalizeText(wo?.counter_measure),
      occurrence: wo?.occurrence,
    })),
  };
}
